import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useDisclosure } from '@reactuses/core';
import { GameLights } from './_components/LightsGameLogic';
import { GameHeader } from '@/components/GameHeader';
import {
  DefaultPopup,
  DefaultPopupProps,
} from '@/components/popups/DefaultPopup';
import { StartGamePopup } from '@/components/popups/StartGamePopup';
import { assets } from '@/lib/assets';
import { useBrowserLeaveActions } from '@/lib/hooks/useBrowserLeaveActions';
import { cn } from '@/lib/utils';
import { playSound, stopSound } from '@/lib/utils/playSound';
import { useTimer } from '@/lib/utils/useTimer';
import { waitForSeconds } from '@/lib/utils/waitForSeconds';
import { gameDataContext } from '@/providers/GameDataProvider';

export const PageLightsGame = () => {
  const wrapper = useRef<HTMLCanvasElement | null>(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setCurrentGameScore, nextGamePath, openProgressPopup } =
    useContext(gameDataContext);
  const [gameStarted, setGameStarted] = useState(false);

  // on brower leave or tab change, we reset the game
  useBrowserLeaveActions({
    onLeaveCallback: () => {
      setCurrentGameScore(0);
    },
  });

  const {
    isOpen: startPopupOpen,
    onClose: close_startPopup,
    onOpen: open_startPopup,
  } = useDisclosure();

  const [infoPopupData, setInfoPopupData] = useState<DefaultPopupProps>({
    title: '',
    description: '',
    buttonLabel: '',
    onButtonClick: () => {
      console.log('continue function not set');
    },
  });

  const {
    isOpen: infoPopup_open,
    onClose: close_infoPopup,
    onOpen: open_infoPopup,
  } = useDisclosure();

  const [gameOver, setGameOver] = useState(false);
  const {
    start: startTimer,
    timeInSec,
    stop: stopTimer,
    reset: resetTimer,
  } = useTimer();

  // --------------------- methods ------------------------

  const showInfoPopup = (data: DefaultPopupProps) => {
    setInfoPopupData(data);
    open_infoPopup();
  };

  const retry = () => {
    setGameOver(false);
    setCurrentGameScore(0);
    close_infoPopup();
    open_startPopup();
  };

  // const onGameContinueAfterBroken = () => {
  //   console.log('continue after broken'); // TODO
  // };

  const handleStartGame = () => {
    GameLights.startGame();
    setGameStarted(true);
  };

  const onGameFailed = () => {
    showInfoPopup({
      title: t('popup.fail.title'),
      description: t('popup.fail.description'),
      buttonLabel: t('common.retry'),
      onButtonClick: () => {
        retry();
      },
    });
  };

  const onGameSuccess = async (finalScore: number) => {
    // game is completed, we add the char and wait a bit for animation
    await waitForSeconds(1);

    showInfoPopup({
      title: t('popup.success.title', { score: finalScore }),
      description: t('popup.success.description', { score: finalScore }),
      buttonLabel: t('popup.success.button'),
      onButtonClick: () => {
        navigate(nextGamePath);
      },
      cancelLabel: t('popup.success.retry'),
      onCancel: () => {
        retry();
      },
    });
  };

  // --------------------- effects ------------------------
  useEffect(() => {
    // init game
    if (wrapper.current) {
      GameLights.initApp(wrapper.current);
    }
    // show popup
    setTimeout(() => {
      openProgressPopup(() => {
        open_startPopup();
      });
    }, 1000);
    // unload destroy
    return () => {
      GameLights.destroyApp();
    };
  }, [navigate]);

  // updage game callbacks
  useEffect(() => {
    GameLights.updateCallbacks({
      onGameStarted: () => {
        resetTimer();
        startTimer();
        // setGameStarted(true);
        close_startPopup();
      },
      onScoreUpdated: (newScore) => setCurrentGameScore(newScore),
      onGameOver: (finalScore) => {
        setGameOver(true);
        stopTimer();
        setCurrentGameScore(finalScore);
        if (finalScore > 0) {
          onGameSuccess(finalScore);
        } else {
          onGameFailed();
        }
      },
      onStatusChanged: (newStatus) => {
        console.log('status changed:', newStatus); //setStatus(newStatus)
      },
      onBrokenLedDisplayed: (callback) => {
        setTimeout(() => {
          showInfoPopup({
            title: t('game_lights.popup.broken.title'),
            description: t('game_lights.popup.broken.description'),
            buttonLabel: t('game_lights.popup.broken.button'),
            onButtonClick: () => {
              close_infoPopup();
              callback();
            },
          });
        }, 1000);
      },
    });
  });

  // play music
  useEffect(() => {
    playSound(assets.game_lights.sounds.music, {
      loop: true,
      volume: 0.2,
    });

    // unload
    return () => {
      stopSound(assets.game_lights.sounds.music, 1000, true);
    };
  }, []);

  // --------------------- render ------------------------

  return (
    <>
      {/* // --------------------- Header ------------------------ */}
      <GameHeader open timeInSec={timeInSec} showTimer={false} />

      {/* // --------------------- game ------------------------ */}
      <div className="absolute left-0 top-0 flex h-screen w-screen justify-center overflow-hidden bg-black">
        <img
          alt="game background"
          src={assets.game_lights.bg}
          className={cn(
            'absolute h-full w-auto object-cover',
            !gameStarted && 'opacity-60 blur-md filter'
          )}
        />
        <canvas className="absolute h-full w-auto" ref={wrapper} />
      </div>

      {/* // --------------------- HUD ------------------------ */}
      <div className="flex h-full overflow-hidden">
        <div className="pointer-events-none z-10 flex w-full justify-between">
          {gameOver && <div className="text-red-600">GAME OVER</div>}
        </div>
        {/* <Stars numStars={score} /> */}
      </div>

      {/* // --------------------- POPUPS ------------------------ */}
      <DefaultPopup
        isOpen={infoPopup_open}
        onClose={close_infoPopup}
        {...infoPopupData}
      />

      <StartGamePopup
        isOpen={startPopupOpen}
        onClose={handleStartGame}
        title={t('game_lights.title')}
        description={t('game_lights.description')}
      />
    </>
  );
};
